import { Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TransferStateService } from '@scullyio/ng-lib';

@Injectable({
  providedIn: 'root',
})
export class CoursePageResolver implements Resolve<any> {
  constructor(
    private http: HttpClient,
    private transferStateService: TransferStateService
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const lang = route.url[0].path;
    const courseName = route.url[2].path;
    let video = '';

    if (route.url.length > 3) {
      video = route.url[3].path;
    }

    if (!lang || !courseName) {
      return of(null);
    }

    // URLs for the main file and the meta-data file
    const contentUrl = `../../../assets/${environment.projectName}/content/${lang}/course-page/courses-data.json`;
    const metadataUrl = `../../../assets/${environment.projectName}/content/${lang}/course-page/meta-data.json`;

    // Use forkJoin to fetch both files in parallel
    return forkJoin({
      course: this.transferStateService.useScullyTransferState(
        'content',
        this.http.get<any>(contentUrl).pipe(
          map((data) => {
            let course = data.find(
              (service: any) =>
                service.title.toLowerCase().replace(/ /g, '-') === courseName.toLowerCase()
            );
            
            if (course) {
              course.courseList.forEach((chapter: any) => {
                chapter.lessons.forEach((lesson: any) => {
                  const pathParts = lesson.path.split('\\');
                  const videoFileName = pathParts[pathParts.length - 1];
                  lesson.played = this.getPlayedStatus(courseName, videoFileName);
                });
              });

              if (video) {
                for (let chapterIndex = 0; chapterIndex < course.courseList.length; chapterIndex++) {
                  const chapter = course.courseList[chapterIndex];

                  for (let lessonIndex = 0; lessonIndex < chapter.lessons.length; lessonIndex++) {
                    const lesson = chapter.lessons[lessonIndex];
                    const pathParts = lesson.path.split('\\');
                    const videoFileName = pathParts[pathParts.length - 1];

                    if (videoFileName === video) {
                      const nextLesson =
                        lessonIndex + 1 < chapter.lessons.length
                          ? chapter.lessons[lessonIndex + 1]
                          : chapterIndex + 1 < course.courseList.length
                          ? course.courseList[chapterIndex + 1].lessons[0]
                          : null;

                      const prevLesson =
                        lessonIndex - 1 >= 0
                          ? chapter.lessons[lessonIndex - 1]
                          : chapterIndex - 1 >= 0
                          ? course.courseList[chapterIndex - 1].lessons[
                              course.courseList[chapterIndex - 1].lessons.length - 1
                            ]
                          : null;

                      lesson.played = true;

                      course.videoLesson = {
                        ...lesson,
                        currentIndex: lessonIndex,
                        currentChapterIndex: chapterIndex,
                        nextLesson,
                        prevLesson,
                      };

                      this.updatePlayedStatus(courseName, videoFileName);
                    }
                  }
                }
              }
            }

            return course || null;
          }),
          catchError((error) => {
            console.error('Error fetching article:', error);
            return of(null);
          })
        )
      ),

      metadata: this.transferStateService.useScullyTransferState(
        'metadata',
        this.http.get<any>(metadataUrl).pipe(
          catchError((error) => {
            console.error('Error fetching metadata:', error);
            return of(null);
          })
        )
      ),
    }).pipe(
      map((result) => {
        return { content: result.course, metadata: result.metadata };
      })
    );
  }

  private getPlayedStatus(courseName: string, videoFileName: string): boolean {
    const playedVideos = JSON.parse(localStorage.getItem('playedVideos') || '{}');
    return playedVideos[`${courseName}-${videoFileName}`] || false;
  }

  private setPlayedStatus(courseName: string, videoFileName: string, played: boolean): void {
    const playedVideos = JSON.parse(localStorage.getItem('playedVideos') || '{}');
    playedVideos[`${courseName}-${videoFileName}`] = played;
    localStorage.setItem('playedVideos', JSON.stringify(playedVideos));
  }

  private updatePlayedStatus(courseName: string, videoFileName: string): void {
    this.setPlayedStatus(courseName, videoFileName, true);
  }
}
